import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ChevronDown, Hourglass } from "lucide-react";
import Marquee from "../Marquee";
import { LOGO_URL, CLUB, STATS, NEXT_MEETING } from "@/data";

function Masked({ children, delay }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function LogoMedallion() {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rx = useSpring(mx, { stiffness: 55, damping: 13 });
    const ry = useSpring(my, { stiffness: 55, damping: 13 });

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        my.set(((e.clientY - r.top) / r.height - 0.5) * -14);
        mx.set(((e.clientX - r.left) / r.width - 0.5) * 16);
    };

    return (
        <motion.div
            className="relative flex items-center justify-center py-6"
            onMouseMove={onMove}
            onMouseLeave={() => {
                mx.set(0);
                my.set(0);
            }}
            style={{ perspective: 900 }}
            data-testid="home-logo-medallion"
        >
            <span className="steam w-40 h-40 bg-[#2a7f7f]/35" style={{ left: "8%", top: "12%", animationDelay: "0s" }} />
            <span className="steam w-32 h-32 bg-[#a8c8bf]/45" style={{ right: "10%", top: "28%", animationDelay: "1.6s" }} />
            <span className="steam w-28 h-28 bg-[#c1603f]/30" style={{ left: "22%", bottom: "6%", animationDelay: "3.1s" }} />

            <motion.img
                src={LOGO_URL}
                alt={`Logo ${CLUB.name}`}
                draggable={false}
                className="relative w-[min(360px,72vw)] select-none"
                style={{
                    mixBlendMode: "multiply",
                    maskImage: "radial-gradient(circle at 50% 50%, black 58%, transparent 78%)",
                    WebkitMaskImage: "radial-gradient(circle at 50% 50%, black 58%, transparent 78%)",
                    rotateX: rx,
                    rotateY: ry,
                }}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                data-testid="home-logo"
            />
        </motion.div>
    );
}

export default function Home({ go }) {
    return (
        <div>
            <section className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-center" data-testid="home-hero">
                <div>
                    <p className="hero-eyebrow">Benvenuti nel tomo</p>
                    <h1 className="hero-title mt-4" data-testid="home-hero-title">
                        <Masked delay={0.1}>
                            <span className="text-[#3f2a12]">Tea &amp; Tales</span>
                        </Masked>
                        <Masked delay={0.28}>
                            <span className="text-[#1c5b5b]">Book Club</span>
                        </Masked>
                    </h1>
                    <motion.p
                        className="f-body italic text-[#6d5638] mt-5 text-lg sm:text-xl max-w-xl"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.7 }}
                    >
                        Storie del tempo e del tè — un circolo di lettori appassionati di{" "}
                        <span className="text-[#9c4a2e] not-italic f-title text-sm tracking-widest uppercase">Fantasy</span> e{" "}
                        <span className="text-[#9c4a2e] not-italic f-title text-sm tracking-widest uppercase">Fantascienza</span>.
                    </motion.p>
                    <motion.div
                        className="flex flex-wrap gap-4 mt-8"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.72, duration: 0.6 }}
                    >
                        <button className="btn-brass" data-testid="cta-next-meeting" onClick={() => go("future")}>
                            Prossime letture <ChevronDown size={16} />
                        </button>
                        <button className="btn-ghost" data-testid="cta-libri" onClick={() => go("libri")}>
                            Sfoglia i libri letti
                        </button>
                    </motion.div>
                </div>
                <LogoMedallion />
            </section>

            <motion.section
                className="brass-frame mt-12 lg:mt-16 bg-[rgba(255,246,224,0.5)]"
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="next-meeting-card"
            >
                <div className="flex flex-col md:flex-row md:items-center gap-6 p-6 sm:p-8">
                    <div className="date-box next" data-testid="next-meeting-date">
                        <span className="day">{NEXT_MEETING.day}</span>
                        <span className="month">{NEXT_MEETING.month}</span>
                    </div>
                    <div className="flex-1">
                        <span className="badge badge-next">
                            <Hourglass size={12} /> Prossimo incontro
                        </span>
                        <h3 className="f-title text-2xl sm:text-3xl text-[#3f2a12] mt-3">{NEXT_MEETING.title}</h3>
                        <p className="f-body italic text-[#6d5638] mt-1">{NEXT_MEETING.by}</p>
                    </div>
                    <p className="f-detail text-[#7a5a2e] md:max-w-[220px] md:text-right text-base">
                        Il prossimo tè è versato: la data la trovi anche nel capitolo Future letture.
                    </p>
                </div>
            </motion.section>

            <section className="grid sm:grid-cols-3 gap-5 mt-8" data-testid="home-stats">
                {STATS.map((s, i) => (
                    <motion.div
                        key={s.id}
                        className="parchment-card p-6 text-center"
                        initial={{ opacity: 0, y: 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.55, delay: i * 0.08 }}
                        data-testid={`summary-${s.id}`}
                    >
                        <s.icon size={26} strokeWidth={1.5} className="mx-auto text-[#8a683c]" />
                        <p className="f-display text-4xl text-[#3f2a12] mt-3">{s.number}</p>
                        <p className="f-title text-[0.7rem] tracking-[0.22em] uppercase text-[#9c4a2e] mt-2">{s.label}</p>
                        <p className="f-body italic text-[#6d5638] mt-2 text-sm">{s.note}</p>
                    </motion.div>
                ))}
            </section>

            <Marquee />
        </div>
    );
}
